import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Container, Row, Col, Card, CardHeader, Modal, ModalHeader, ModalBody, Label, ModalFooter, Input, Button } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";

import { useGetInspectionDownloadUidsMutation, useGenerateReportsPDFByUuidMutation } from "slices/safetyApp/safetyappSlice";

// Import Breadcrumb
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DeleteModal from "../../../Components/Common/DeleteModal";

import { useGetInspectionReportsQuery } from "slices/InspectionReport/InspectionReportSlice";

import { REPORT_STATUS, ReportStatusId } from "utils/CommonUtils";

// Redux
import { useSelector, useDispatch } from "react-redux";
import TableContainer from "../../../Components/Common/TableContainer";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Export Modal
import ExportCSVModal from "../../../Components/Common/ExportCSVModal";
import { createSelector } from "reselect";
import Loader from "Components/Common/Loader";
import { useGetProjectsByDropDownQuery } from "slices/projects/projectSlice";
import { Tooltip } from "react-tooltip";
import { formatDateTime } from "helpers/date_utils";
import DownloadModal from "Components/Common/DownloadModal";
import { usePdfDownloader } from "Components/Hooks/usePdfDownloader";
import { IsValidPermissions } from "Routes/AuthProtected";


const InspectionReportListView = () => {
  const [page, setPageState] = useState<{
    page: number;
    pageSize: number;
    search: string;
    report_status: string;
    start_date: string;
    end_date: string;
    project: string;
  }>({
    page: 1,
    pageSize: 10,
    search: "",
    report_status: "",
    start_date: "",
    end_date: "",
    project: "",
  });
  const [generateReportsPDFByUuid, { isLoading: isPdfLoading }] = useGenerateReportsPDFByUuidMutation();
  const [getPermitDownloadUids, { isLoading: isUidLoading }] = useGetInspectionDownloadUidsMutation();

  const {
    downloadState,
    setDownloadState,
    handleDownload
  } = usePdfDownloader({
    getPermitDownloadUids,
    generateReportsPDFByUuid
  });

  const {
    data: inspectionReportData,
    isLoading: inspectionReportloading,
    refetch: refetchinspectionReport,
    isFetching: inspectionreportFetching
  } = useGetInspectionReportsQuery(page);

  const { data: projectData } = useGetProjectsByDropDownQuery(undefined);

  // State for Modal and Selected Project
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  // Toggle Modal
  const toggleModal = useCallback(() => {
    setIsModalOpen((prev) => !prev);
  }, []);

  // Redirect to Home Work Permit Wizard with Project ID in the URL
  const handleCreateNewReport = () => {
    if (selectedProject) {
      navigate(`/inspection-report/${selectedProject}`);
    }
  };

  // Map Projects to Dropdown Options
  const projectOptions = useMemo(() => {
    return (
      projectData?.map((project: any) => ({
        value: project.id,
        label: `${project.project_name} (${project.project_short_name})`,
      })) || []
    );
  }, [projectData]);


  const navigate = useNavigate();

  const [customer, setCustomer] = useState<any>([]);
  const [modal, setModal] = useState<boolean>(false);

  const toggle = useCallback(() => {
    setModal((prevModal) => !prevModal);
    if (!modal) {
      setCustomer(null);
    }
  }, [modal]);

  // Add Customer
  const handleCustomerClicks = () => {
    setCustomer(null);
    toggle();
  };
  const [reportData, setReportData] = useState<any>([]);

  useEffect(() => {
    if (inspectionReportData) {
      setReportData(inspectionReportData);
    }
  }, [inspectionReportData]);



  const columns = useMemo(
    () => [
      {
        Header: "Report ID",
        accessor: "report_id",
        filterable: false,
      },
      {
        Header: "Project Name",
        accessor: "project_name",
        filterable: false,
      },
      {
        Header: "Start & End Date",
        accessor: "start_date_of_work", // Start Date
        Cell: ({
          row: {
            original: {
              start_date,
              end_date,
            },
          },
        }: any) => (
          <>
            {formatDateTime(start_date, "date")}{" "}
            <i className="las la-arrows-alt-h"></i>{" "}
            {formatDateTime(end_date, "date")}{" "}
          </>
        ),
      },
      {
        Header: "Report Status",
        accessor: "report_status",
        filterable: false,
        Cell: (cell: any) => {
          const statusId = cell.value as ReportStatusId;
          const statusName = REPORT_STATUS[statusId] || "Unknown";
          let badgeClass = "badge bg-secondary text-dark";

          switch (statusId) {
            case 1: // Draft
              badgeClass = "badge bg-primary-subtle text-primary";
              break;
            case 2: // In Progress
            case 3: // Assigned
              badgeClass = "badge bg-info-subtle text-info";
              break;
            case 4: // Pending Review
            case 5: // In Review
              badgeClass = "badge bg-warning-subtle text-warning";
              break;
            case 6: // Completed
              badgeClass = "badge bg-success-subtle text-success";
              break;
            case 7: // Cancelled
            case 8: // On Hold
              badgeClass = "badge bg-danger-subtle text-danger";
              break;
            default:
              badgeClass = "badge bg-secondary text-dark";
          }

          return <span className={badgeClass}>{statusName}</span>;
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }: any) => {

          const reportStatus = row?.original?.report_status_name;

          const handleView = () => {
            navigate(`/inspection-report-view/${row?.original?.project}/${row?.original?.report_id}?view_report=true`);
          };
          const handleReportEdit = () => {
            navigate(`/inspection-report/${row?.original?.project}/${row?.original?.report_id}/`);
          };

          return (
            <ul className="list-inline hstack gap-2 mb-0">
              {IsValidPermissions(["VIEW_ONLY_INSPECTION_REPORT"]) && (reportStatus === "Open" || reportStatus === "Action Completed") && (
                <li className="list-inline-item edit" title="View">
                  <div
                    className="text-primary d-inline-block edit-item-btn"
                    onClick={handleView}
                  >
                    <i className="ri-eye-fill fs-16"></i>
                  </div>
                </li>
              )}
              {IsValidPermissions(["EDIT_INSPECTION_REPORT"]) && (reportStatus === "Open" || reportStatus === "Draft")}
              <li className="list-inline-item edit" title="View">
                <div
                  className="text-primary d-inline-block edit-item-btn"
                  onClick={handleReportEdit}
                >
                  <i className="ri-pencil-fill fs-16"></i>
                </div>
              </li>
              {IsValidPermissions(["DOWNLOAD_INSPECTION_REPORT"]) &&
                (reportStatus === "Completed") ? (
                <li className="list-inline-item edit" title="Export Permit">
                  <Link
                    to={`#`}
                    className="text-primary d-inline-block edit-item-btn export-task-status-class"
                    onClick={() => handleDownload(row?.original?.report_id)}
                  >
                    <i className="ri-download-fill"></i>
                  </Link>
                </li>
              ) : (
                <></>
              )}
            </ul>
          );
        },
      },
    ],
    [navigate],
  );

  // Export Modal
  const [isExportCSV, setIsExportCSV] = useState<boolean>(false);

  document.title = "Customers";
  return (
    <React.Fragment>
      <Tooltip
        style={{ zIndex: 10 }}
        anchorSelect=".export-task-status-class"
        place="bottom"
        content="Export Reports"
      />
      <Tooltip
        style={{ zIndex: 10 }}
        anchorSelect=".view-task-status-class"
        place="bottom"
        content="View"
      />
      <Tooltip
        style={{ zIndex: 10 }}
        anchorSelect=".edit-task-status-class"
        place="bottom"
        content="Edit"
      />
      <div className="page-content">
        {/* <ExportCSVModal
          show={isExportCSV}
          onCloseClick={() => setIsExportCSV(false)}
          data={reportData}
          columns={columns}
          name={"Report"}
        /> */}
        <DeleteModal
          show={modal}
          onDeleteClick={handleCustomerClicks}
          onCloseClick={() => setModal(false)}
        />
        <Container fluid>
          <BreadCrumb title="Report List" pageTitle="Inspection Report" />
          <Row>
            <Col lg={12}>
              <Card id="customerList">
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <div className="col-sm">
                      <div>
                        <h5 className="card-title mb-0">Report List</h5>
                      </div>
                    </div>
                    <div className="col-sm-auto">
                      <div>
                        <button
                          type="button"
                          className="btn btn-success add-btn me-1"
                          id="create-btn"
                          onClick={toggleModal}
                          disabled={inspectionReportloading}
                        >
                          <i className="ri-add-line align-bottom me-1"></i> Add
                          Report
                        </button>{" "}
                        {/* <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setIsExportCSV(true)}
                          disabled={
                            inspectionReportloading || reportData?.length === 0
                          }
                        >
                          <i className="ri-file-download-line align-bottom me-1"></i>{" "}
                          Export
                        </button> */}
                      </div>
                    </div>
                  </Row>
                </CardHeader>
                <div className="card-body pt-0">
                  {inspectionReportloading ? (
                    <Loader />
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={inspectionReportData?.results || []}
                      isLoading={inspectionreportFetching}
                      isGlobalFilter={true}
                      isAddUserList={false}
                      customPageSize={page.pageSize}
                      className="custom-header-css"
                      divClass="table-responsive table-card mb-3"
                      tableClass="align-middle table-nowrap mb-0"
                      theadClass="table-light text-muted"
                      thClass="table-light text-muted"
                      // isPermitFilter={true}
                      SearchPlaceholder="Search for Inspection Report..."
                      isPagination={false}
                      isServerPagination={true}
                      serverPaginationProps={{
                        count: inspectionReportData?.count || 0,
                        next: inspectionReportData?.next || null,
                        page_size: inspectionReportData?.page_size || 10,
                        previous: inspectionReportData?.previous || null,
                        current_page: inspectionReportData?.current_page || 1,
                        total_page: inspectionReportData?.total_page || 0,
                      }}
                      setPageState={setPageState}
                    />
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer />
      {/* Modal for Creating New Hot Work Permit */}
      <Modal isOpen={isModalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          Create New Hot Work Permit
        </ModalHeader>
        <ModalBody>
          <Label for="projectSelect">Project</Label>
          <Input
            type="select"
            id="projectSelect"
            onChange={(e) => setSelectedProject(e.target.value)}
            value={selectedProject || ""}
          >
            <option value="">Select Project</option>
            {projectOptions.map((project: any) => (
              <option key={project.value} value={project.value}>
                {project.label}
              </option>
            ))}
          </Input>
        </ModalBody>
        <ModalFooter>
          <Button
            color="success"
            onClick={handleCreateNewReport}
            disabled={!selectedProject}
          >
            Create New Report
          </Button>
          <Button color="danger" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      <DownloadModal
        isOpen={downloadState.isOpen}
        toggle={() => setDownloadState(prev => ({ ...prev, isOpen: false }))}
        status={downloadState.status}
        permitNo={downloadState.permitNo}
        errorMessage={downloadState.errorMessage}
      />
    </React.Fragment>
  );
};

export default InspectionReportListView;
